import type { InvoiceFormValues } from "./invoice-form.types";

// Single source of truth for "is this draft complete enough to be promoted
// to a ready/created bill" — used both by invoice-form's own onSubmit and by
// create-invoice.page.tsx's navigate-away autosave path. A previous, separate
// copy in create-invoice.page.tsx used `&&` instead of `||` in the early
// return, which silently passed drafts with buyer_name+payment_mode but no
// billed_items — keep this the only implementation.
export function validateInvoice(
	buyer_name?: string,
	billed_items?: InvoiceFormValues["billed_items"],
	payment_mode?: string,
): boolean {
	const isBuyerNameNotEmpty = !!buyer_name && buyer_name.trim() !== "";
	const isBilledItemsNotEmpty =
		Array.isArray(billed_items) && billed_items?.length > 0;
	const isPaymentModeNotEmpty = !!payment_mode && payment_mode.trim() !== "";

	if (
		!isBuyerNameNotEmpty ||
		!isBilledItemsNotEmpty ||
		!isPaymentModeNotEmpty
	) {
		return false; // All conditions are not met
	}

	if (isBilledItemsNotEmpty) {
		const areBilledItemsFieldsNotEmpty = billed_items.every(
			(item) =>
				item.item_name &&
				item.item_name.trim() !== "" &&
				item.item_price !== undefined &&
				Number(item.item_price) > 0 &&
				item.item_qty !== undefined &&
				Number(item.item_qty) > 0,
		);

		return (
			isBuyerNameNotEmpty && areBilledItemsFieldsNotEmpty && isPaymentModeNotEmpty
		);
	}

	return isBuyerNameNotEmpty && isPaymentModeNotEmpty;
}

// Single source of truth for turning a formik snapshot into a persistable
// Draft payload. Every code path that writes a draft to the backend (or
// stages one locally) must go through this — not just invoice-form's own
// Save/Ready/Create Bill submit, but also create-invoice.page.tsx's
// navigate-away autosave, which previously sent raw, unconverted `values`
// straight to the BE (unparsed discount/service-charge/ESF fields, no
// buyer_pan/invoice_number stringification, no customer_info-derived
// buyer_name/buyer_pan reconciliation).
export function sanitizeDraftPayload(
	currentDraft: (Partial<InvoiceFormValues> & Record<string, unknown>) | null,
	values: InvoiceFormValues,
): Record<string, unknown> {
	// While a customer is linked, buyer_name/buyer_pan are re-derived from
	// customer_info here rather than trusted as accumulated in `values` —
	// customer_info is refreshed whenever the linked customer's profile is
	// edited mid-session (see CustomerSelector's handleCustomerSelection
	// profile-update branch), but that refresh deliberately doesn't overwrite
	// buyer_name/buyer_pan mid-session. This is the one place that reconciles
	// them, at the moment the draft/bill is actually persisted. Unlinked
	// (walk-in) buyers keep their directly-typed buyer_name/buyer_pan.
	const buyerName = values.company_customer_id
		? values.customer_info?.customer_name || values.buyer_name
		: values.buyer_name;
	const buyerPan = values.company_customer_id
		? values.customer_info?.customer_pan_vat ?? null
		: values.buyer_pan;

	const draft_valid = validateInvoice(
		buyerName,
		values?.billed_items,
		values?.payment_mode,
	);

	return {
		...currentDraft,
		...values,
		buyer_name: buyerName,
		customer_mobile: values.customer_mobile ? values.customer_mobile : null,
		buyer_pan: buyerPan?.toString() || null,
		invoice_number: values.invoice_number?.toString(),
		non_taxable_discount: parseFloat(String(values?.non_taxable_discount || 0)),
		taxable_discount: parseFloat(String(values?.taxable_discount || 0)),
		non_taxable_service_charge: parseFloat(
			String(values?.non_taxable_service_charge || 0),
		),
		taxable_service_charge: parseFloat(
			String(values?.taxable_service_charge || 0),
		),
		amount_for_esf: parseFloat(String(values?.amount_for_esf || 0)),
		esf: parseFloat(String(values?.esf || 0)),
		draft_valid,
	};
}
